import { Component, HostListener, inject, OnInit, SecurityContext } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { BsModalService } from 'ngx-bootstrap/modal';
import { CookieService } from 'ngx-cookie';
import { EMPTY, finalize, first, forkJoin, map, of, switchMap, zip } from 'rxjs';

import { ContentSlide } from '@bn/auth/models/sliders/content-slide.model';
import { AuthService } from '@bn/auth/services/auth.service';
import { AuthSliderService } from '@bn/auth/services/auth-slider.service';
import { LastUsedProviderService } from '@bn/auth/services/last-used-provider.service';
import { UserDetailsStateService } from '@bn/auth/services/user-details-state.service';
import {
  APP_CONFIG,
  ApplicationRoles,
  AppRoutes,
  AuthStatus,
  ClickActionEvent,
  EmailValidator,
  ENV_CONFIG,
  sanitizeReturnUrl,
  SSOService,
  SSOUrlList,
  StringStringMap,
  StringTMap,
  UtmData,
} from '@bn/primitives';
import { UserAuth, UserDetails } from '@bn/shared/core/api';
import { MatomoService, PosthogService } from '@bn/shared/core/services';
import { MixpanelEventsDirective } from '@bn/shared/directives';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { InputAction } from '@bn/web-ui/input/enums';
import { InputOptions } from '@bn/web-ui/input/models';

export const WHITE_LIST_RETURN_URL_DOMAINS = {
  cli: ['localhost', '127.0.0.1'],
  discord: ['discord.com', 'api.bunny.net'],
};

@Component({
  selector: 'bn-auth-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  host: { class: 'bn-auth__page-container' },
  standalone: false,
})
export class LoginPage implements OnInit {
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #APP_CONFIG = inject(APP_CONFIG);
  readonly #authService = inject(AuthService);
  readonly #authSliderService = inject(AuthSliderService);
  readonly #authStorageKey = this.#authService.getAuthStoreKey();
  readonly #cookie = inject(CookieService);
  readonly #ENV_CONFIG = inject(ENV_CONFIG);
  readonly #fb = inject(NonNullableFormBuilder);
  readonly #lastUsedProviderService = inject(LastUsedProviderService);
  readonly #matomo = inject(MatomoService);
  readonly #mixpanel = inject(MixpanelEventsDirective);
  readonly #modal = inject(BsModalService);
  readonly #posthog = inject(PosthogService);
  readonly #router = inject(Router);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #userDetailsService = inject(UserDetailsStateService);

  domainInfo = '';
  unsafeDomainWarning = '';
  public isLoading = {
    initial: true,
    submit: false,
  };
  public isError = false;
  public error?: string;
  public errorMessage?: string;
  public loginForm = this.#fb.group({
    email: this.#fb.control<string>('', [Validators.required, Validators.email, EmailValidator.emailDomain()]),
    password: this.#fb.control<string>('', [Validators.required, Validators.minLength(5)]),
  });
  public isExtAuthMode = false;
  public isUserLoggedIn: null | boolean = null;
  public pageText: StringStringMap = {
    title: 'Welcome back!',
    description: 'Log in and pick up where you left off.',
    login: 'Log In',
  };
  public wpQueries: StringStringMap = {};
  public isSubUser = false;
  public readonly lastUsedProvider = this.#lastUsedProviderService.get();
  public readonly SSOService = SSOService;

  public sliderConfig?: ContentSlide[];
  public readonly ssoUrlList: SSOUrlList = {};
  public readonly passwordInputSettings: Partial<InputOptions> = {
    hideRequiredLabel: true,
    rightAddons: [{ icon: 'eye', action: InputAction.ShowPassword }],
  };
  public redirectUrl = '';
  public extAuthConfig: { confirmDescription: string; confirmButton: string } = { confirmDescription: '', confirmButton: '' };

  @HostListener('window:storage', ['$event'])
  localStorageChanged(event: StorageEvent): void {
    if (event.key !== this.#authStorageKey || !event.newValue) return;
    document.location.reload();
  }

  constructor() {
    this.#authSliderService.getLoginSlides().subscribe({
      next: (sliderConfig: ContentSlide[]) => {
        this.sliderConfig = sliderConfig;
        this.isLoading.initial = false;
      },
      error: () => (this.isLoading.initial = false),
    });

    const { error: errorMessage, token, returnUrl } = this.#activatedRoute.snapshot.queryParams;

    let selfUrl = returnUrl
      ? `${window.location.origin}${window.location.pathname}?returnFromSSO=true&returnUrl=${encodeURIComponent(returnUrl)}`
      : `${window.location.origin}${window.location.pathname}?returnFromSSO=true`;

    selfUrl = encodeURIComponent(selfUrl);

    if (errorMessage) {
      this.error = 'Error logging in';
      this.errorMessage = errorMessage;
    }

    this.ssoUrlList.google = `${this.#ENV_CONFIG.apiUrl}/openid/${SSOService.Google}?callbackUrl=${selfUrl}&errorUrl=${selfUrl}`;
    this.ssoUrlList.gitlab = `${this.#ENV_CONFIG.apiUrl}/openid/${SSOService.Gitlab}?callbackUrl=${selfUrl}&errorUrl=${selfUrl}`;
    this.ssoUrlList.github = `${this.#ENV_CONFIG.apiUrl}/oauth/${SSOService.Github}?callbackUrl=${selfUrl}&errorUrl=${selfUrl}`;

    if (token) {
      this.tokenLogin(token);
    }
  }

  public ngOnInit(): void {
    const { callbackUrl, domain, source, returnFromSSO, user } = this.#activatedRoute.snapshot.queryParams;

    // TODO: make some kind of configuration with all of the texts, image assets and possible ?source values
    // TODO: don't duplicate logic in register.page
    if (source && domain && callbackUrl) {
      // came directly from auth referrer
      this.initExtAuth();
    } else if (this.#cookie.hasKey(this.#APP_CONFIG.extAuthCookieName) && returnFromSSO) {
      // came back from sso
      if (!this.wpQueries.callbackUrl) {
        const cookieWpQueriesString = this.#cookie.get(this.#APP_CONFIG.extAuthCookieName);

        if (cookieWpQueriesString) {
          this.wpQueries = JSON.parse(cookieWpQueriesString);
          this.#authService.removeCookieAndRedirect(this.redirectUrl);
        }
      }
    } else if (this.#authService.isLoggedIn()) {
      this.#authService.navigateAfterLogin('/').subscribe();
    }

    if (user) {
      const queryEmail = decodeURIComponent(user);

      // TODO: Should we validate email here?
      this.loginForm.get('email')?.setValue(queryEmail);
    }
  }

  public onSubmit(): void {
    if (this.loginForm.invalid || this.isLoading.submit) return;

    const errorText = 'Login failed.';
    this.isLoading.submit = true;
    this.isError = false;
    this.error = undefined;

    const utm: UtmData = this.getUtmData();
    const loginValues = this.loginForm.getRawValue();
    this.#authService
      .login(loginValues.email.trim(), loginValues.password.trim(), utm, this.isExtAuthMode)
      .pipe(
        switchMap((authStorage) => {
          // End early if auth fails
          if (authStorage.auth_status === AuthStatus.Invalid || !authStorage.auth_token) {
            if (this.isExtAuthMode) {
              this.isSubUser = authStorage?.isSubuser ?? false;
              this.isUserLoggedIn = authStorage?.isSubuser ?? false;
            } else this.setError(errorText);

            this.isLoading.submit = false;

            return EMPTY;
          }

          this.#lastUsedProviderService.set('email');

          const has2FA = authStorage.auth_status === AuthStatus.TwoFactorRequired;

          if (this.isExtAuthMode && !has2FA) {
            return of({ has2FA });
          }

          const returnUrl = this.#sanitizer.sanitize(SecurityContext.URL, this.#activatedRoute.snapshot.queryParams['returnUrl']) ?? '/';

          return forkJoin({
            has2FA: of(has2FA),
            redirectSuccess: this.#authService.navigateAfterLogin(returnUrl),
          });
        }),
        switchMap(({ has2FA }) => (!has2FA && this.isExtAuthMode ? this.#authService.getApiKeyValue().pipe(map((apiKey) => apiKey)) : of(null))),
        finalize(() => (this.isLoading.submit = false)),
      )
      .subscribe({
        next: (apiKey) => {
          if (apiKey) this.#authService.removeCookieAndRedirect(this.#authService.getCallbackUrl(apiKey));
        },
        error: (error) => {
          // Api doesn't explicitly say subuser can't login, but it is external page, we presume it is subuser, logout user
          if (this.isExtAuthMode) this.isSubUser = true;

          this.setError(`${errorText} ${error.error?.Message ?? error?.message ?? ''}`);
        },
      });
  }

  public setLastUsedProvider(provider: SSOService): void {
    this.#lastUsedProviderService.set(provider);
  }

  public extAuthChangeUser(): void {
    this.#authService.logout(undefined, false);
    this.isUserLoggedIn = false; // NOTE: can't just window.location.reload, because we loose referrer
  }

  public extAuthCancel(): void {
    this.#cookie.remove(this.#APP_CONFIG.extAuthCookieName);
    this.#router.navigate(['/']);
  }

  private tokenLogin(token: string): void {
    // Note: "isWelcome" is set by google SSO when users register
    const isWelcome = this.#activatedRoute.snapshot.queryParams?.isWelcome === 'True';

    this.isLoading.submit = true;

    this.#authService.tokenLogin(token).subscribe((userAuth: UserAuth | false) => {
      if (!userAuth) {
        this.setError('Token not valid', 'There was a problem validating token. Try again or contact our support.');

        this.isLoading.submit = false;

        return;
      }

      if (userAuth.AuthStatus === AuthStatus.Authenticated) {
        if (isWelcome) {
          this.#matomo.trackEvent('Registration', 'Sign Up - SSO');
          this.#posthog.captureEvent('Sign Up', { type: 'SSO' });
          this.#mixpanel.addEvent('Sign Up', { type: 'SSO' });
        }
        // get return url from query parameters or default to home page
        // Note: Simple register pool will anyway redirect users on welcome after they finish
        const returnUrl = isWelcome ? AppRoutes.SimpleRegisterPoll : (this.#activatedRoute.snapshot.queryParams.returnUrl ?? '/');
        const cookieWpQueriesString = this.#cookie.get(this.#APP_CONFIG.extAuthCookieName);

        if (cookieWpQueriesString) {
          this.wpQueries = JSON.parse(cookieWpQueriesString);

          this.#authService.removeCookieAndRedirect(this.redirectUrl);

          return;
        }

        this.#authService.navigateAfterLogin(returnUrl).subscribe();
      }
    });
  }

  private setError(error?: string, errorMessage?: string): void {
    this.error = error;
    this.errorMessage = errorMessage;
  }

  private initExtAuth(): void {
    this.isUserLoggedIn = this.#authService.isLoggedIn();

    const queryParams: StringTMap<string> = this.#activatedRoute.snapshot.queryParams;

    this.#cookie.put(this.#APP_CONFIG.extAuthCookieName, JSON.stringify(queryParams));

    zip([this.#userDetailsService.getFreshUserDetails(), this.#authService.getApiKeyValue()]).subscribe(
      ([userDetails, apiKey]: [UserDetails, string | undefined]) => {
        if (userDetails.Roles.includes(ApplicationRoles.Subuser)) this.isSubUser = true;
        if (userDetails.Email) this.pageText.userEmail = userDetails.Email;
        if (!apiKey) return;

        this.redirectUrl = this.#authService.getCallbackUrl(apiKey);
      },
    );

    this.isExtAuthMode = true;
    this.wpQueries = this.#activatedRoute.snapshot.queryParams;
    const url = this.wpQueries.callbackUrl;
    const domain = new URL(url).hostname;
    this.domainInfo = sanitizeReturnUrl(domain) ?? '';

    // Check potential phishing attempt
    if (this.wpQueries.source === 'discord') {
      const hasDiscordDomain = WHITE_LIST_RETURN_URL_DOMAINS.discord.some((discordDomain) => domain.endsWith(discordDomain));
      // Show warning if ?source=discord but domain is not in whitelist
      if (!hasDiscordDomain) this.unsafeDomainWarning = sanitizeReturnUrl(domain) || '';
    } else if (this.wpQueries.source === 'cli') {
      const hasCliDomain = WHITE_LIST_RETURN_URL_DOMAINS.cli.some((cliDomain) => domain.endsWith(cliDomain));
      // Show warning if ?source=cli but domain is not in whitelist
      if (!hasCliDomain) this.unsafeDomainWarning = sanitizeReturnUrl(domain) || '';
    } else if (this.wpQueries.source === 'wp-plugin') {
      // TODO: No checks for wp-plugin source, as we currently don't have a safe list
    } else if (this.wpQueries.source) {
      // Generic warning for unknown sources, as we have no information about them, and they could be potential phishing attempts
      this.unsafeDomainWarning = sanitizeReturnUrl(domain) || '';
      this.pageText = {
        description: `Unknown Plugin Authorization at`,
        description2: `You will authorize an unknown plugin at`,
        login: 'Log in And Authorize',
        submit: 'Create an Account And Authorize',
      };
    }

    const extAuthConfig: Record<string, { description: string; description2: string; login: string; confirmDescription: string; confirmButton: string }> = {
      cli: {
        description: 'CLI Authorization',
        description2: `You will authorize the bunny.net CLI to access your account at`,
        login: 'Log in And Authorize',
        confirmDescription: `Are you sure you want to authorize the bunny.net CLI to access your account from <br><strong>${this.domainInfo}</strong>? <br><br> <strong>Only continue if you trust it.</strong>`,
        confirmButton: 'Authorize',
      },
      discord: {
        description: 'Connect Discord with bunny.net',
        description2: `You will connect your Discord account to your bunny.net account at`,
        login: 'Log in And Connect',
        confirmDescription: `Are you sure you want to connect your Discord account to your bunny.net account from <br><strong>${this.domainInfo}</strong>? <br><br> <strong>Only continue if you trust it.</strong>`,
        confirmButton: 'Connect',
      },
      'wp-plugin': {
        description: 'WordPress Plugin Authorization',
        description2: `You will authorize WordPress plugin at`,
        login: 'Log in And Authorize',
        confirmDescription: `Are you sure you want to authorize plugin from <br><strong>${this.domainInfo}</strong>? <br><br> <strong>Only continue if you trust it.</strong>`,
        confirmButton: 'Authorize',
      },
      unknown: {
        description: 'Unknown Plugin Authorization',
        description2: `You are attempting to authorize an unknown plugin at`,
        login: 'Log in And Authorize',
        confirmDescription: `Are you sure you want to authorize an unknown plugin from <br><strong>${this.domainInfo}</strong>? <br><br> <strong>Only continue if you trust it.</strong>`,
        confirmButton: 'Authorize',
      },
    };

    const config = extAuthConfig[this.wpQueries.source] ?? extAuthConfig['wp-plugin'];

    this.extAuthConfig = config;
    this.pageText = {
      description: config.description,
      description2: config.description2,
      login: config.login,
    };
  }

  private getUtmData(): UtmData {
    return {
      gclid: this.#cookie.get('gclid'),
      twclid: this.#cookie.get('twclid'),
      rdt_cid: this.#cookie.get('rdt_cid'),
      msclkid: this.#cookie.get('msclkid'),
      fbclid: this.#cookie.get('fbclid'),
      liclid: this.#cookie.get('liclid'),
      pk_campaign: this.#cookie.get('pk_campaign'),
      pk_keyword: this.#cookie.get('pk_keyword'),
      device: this.#cookie.get('device'),
      pk_medium: this.#cookie.get('pk_medium'),
      pk_source: this.#cookie.get('pk_source'),
      pk_cid: this.#cookie.get('pk_cid'),
      pk_buttonurl: this.#cookie.get('pk_buttonurl'),
      pk_buttonlocation: this.#cookie.get('pk_buttonlocation'),
      bunnyab: this.#cookie.get('bunnyab'),
      ref_domain: this.#cookie.get('ref_domain'),
    };
  }

  openConfirmationModal(): void {
    this.#modal
      .show(ConfirmationDialogComponent, {
        class: 'modal-dialog-centered modal-sm',
        initialState: {
          data: {
            description: this.extAuthConfig.confirmDescription,
            title: 'Log in and authorize',
            confirmButtonName: this.extAuthConfig.confirmButton,
            contextType: 'danger',
            event: {
              action: 'confirm',
            },
            textCenter: true,
          },
        },
      })
      .content?.getOnAction$()
      .pipe(
        first(),
        finalize(() => this.#modal.hide()),
      )
      .subscribe(({ action }: ClickActionEvent) => {
        if (action === 'confirm') this.#authService.removeCookieAndRedirect(this.redirectUrl);
      });
  }
}
